import { Nutrients, NutrientTargets } from "./data-model/food";

export interface MealTunerFood {
  nutrientsPer_100g?: Nutrients /** not used on result */;
}

export interface MealTunerFoodMap {
  [foodId: string]: MealTunerFood;
}

export interface MealTunerIngredient {
  foodId: string;
  gramsPerMeasure: number;
  measures: number;
  fractions?: number;
  locked: boolean;
  nutrients?: Nutrients; // This is only used if ingredient is a custom macros
  tunedMeasures?: number /** only used on result */;
}

export interface MealTunerMeal {
  id: string;
  ingredients: MealTunerIngredient[];
  precision_pc?: number /** only used on result */;
  rescaled?: number /** debug value used on result */;
  nutrients?: Nutrients /** only used on result */;
}

export interface MealTunerParams {
  targets: NutrientTargets;
  foods: MealTunerFoodMap;
  meals: MealTunerMeal[];
  /**
   * true when `meals` are candidates to choose from (meal suggestions, auto-planning) rather than
   * the one meal the user asked to tune. decides what a meal with broken numbers means: for a
   * candidate list it is only listed in `invalidMeals`; for the user's own meal it also fails the
   * solve through `error`.
   */
  candidates?: boolean;
}

/** A meal the tuner left out because one of its rows carried a number it cannot use (NaN or Infinity). */
export interface MealTunerInvalidMeal {
  id: string;
  /** which row, and which of its numbers, was broken -- no user text */
  detail: string;
}

/**
 * Whether a tuner answer is an answer at all.
 *
 * A string enum rather than a boolean `ok` flag for a reason that is not style: this project
 * compiles with `strictNullChecks` off, and under that setting TypeScript does NOT narrow a union
 * by a boolean discriminant -- `if (!result.ok)` leaves the type exactly as wide as it was, which
 * would make the whole shape below decorative. A literal discriminant narrows correctly.
 */
export enum MealTunerOutcome {
  /** the solve ran and produced an answer, even if that answer is "nothing fits" */
  Solved = 'solved',
  /** the solve did not produce an answer at all */
  Failed = 'failed',
}

/** The parts of a tuner answer that are there whether the solve ran or crashed. */
interface MealTunerResultsCommon {
  debug: string;
  params?: MealTunerParams; // useful for some debugging
}

/**
 * The solve ran. `meals` is its answer, and an EMPTY list here is a real answer -- it means no
 * combination of these foods fits the targets, which is something the user can act on.
 */
export interface MealTunerSolved extends MealTunerResultsCommon {
  outcome: MealTunerOutcome.Solved;
  meals: MealTunerMeal[];
  /** meals left out because their numbers were broken; absent when there were none */
  invalidMeals?: MealTunerInvalidMeal[];
}

/**
 * The solve failed: either the tuner threw, or the one meal the user asked to tune carried a
 * number the tuner cannot use. There is no answer to read, so there is no `meals` field at all.
 * `error` is never empty -- `solve` substitutes its own wording when it catches something that
 * describes itself as ''.
 */
export interface MealTunerFailed extends MealTunerResultsCommon {
  outcome: MealTunerOutcome.Failed;
  error: string;
}

/**
 * What `MealTuner.solve` answers.
 *
 * This is a discriminated union rather than one flat object with an `error` string, and the
 * missing `meals` on the failing branch is the whole point: a crash and an honest "no combination
 * of these foods fits" used to arrive as the same empty meal list, so a caller that forgot to
 * compare `error` against '' read our own arithmetic failing as an ordinary negative answer and
 * told the user something about their food that we had no basis for (#5297). Every caller had to
 * be fixed by hand once already (df-ee0413ba15b2). Now `results.meals` does not compile until the
 * caller has narrowed on `outcome`, so forgetting is a build failure instead of a wrong answer.
 *
 * `solve` CATCHES its own crash rather than letting it travel, and that is decided, not left over
 * (#5776). Each of its callers handles a failed answer in the way its own screen needs: the day
 * planner skips one candidate, Auto-Tune tells the user and undoes a split, the split-and-tune
 * paths explain it, and the suggestions page throws into its own catch. Every one of them reports
 * the failure to Sentry. An escaping exception would give them nothing they lack, and would cost
 * the day planner a whole day's suggestions for one bad candidate. The same catch also turns the
 * deliberate "Target energy not specified" throw into a failed answer. A caller that wants an
 * exception throws one itself, as `meal-suggestions.ts` does.
 */
export type MealTunerResults = MealTunerSolved | MealTunerFailed;
